import { getDataSourceExecutions, isConfigurationAllowed } from '@/api';
import {
  Button,
  Grid,
  Icon,
  Modal,
  Table,
  Tooltip,
  Typography,
} from '@prometheus/components';
import { ModalContext, UseModalContext, useList } from '@prometheus/hooks';
import { Status } from '@prometheus/types';
import { FC, useCallback, useContext, useMemo } from 'react';
import { ExecutionModal } from '../ExecutionModal';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { ExecutionResults } from '../ExecutionResults/ExecutionResults';
import { CdmDataSourceDTO } from '@/types';
import { columns } from './ExecutionsList.config';
import { useTranslation } from 'react-i18next';
export const ExecutionsList: FC<{
  dataSourceId: string;
  dataSource: CdmDataSourceDTO;
}> = ({ dataSourceId, dataSource }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, status, getEntityList, totalElements, pageCount } = useList({
    primaryKey: 'id',
    autoLoad: false,
    methods: {
      read: {
        method: ({ pageNumber, pageSize, sortBy }) =>
          getDataSourceExecutions(dataSourceId)({
            pageNumber,
            pageSize,
            sortBy,
          }),
      },
    },
  });
  const { openModal, closeModal } = useContext<UseModalContext>(ModalContext);

  const handleExecute = async () => {
    const result = await isConfigurationAllowed(dataSourceId);
    openModal(
      () => (
        <ExecutionModal
          dataSourceId={dataSourceId}
          onClose={closeModal}
          reloadList={getEntityList}
          isExecutionAllowed={result?.allowed}
        />
      ),
      t('modals.create_job_database_header'),
      {
        closeOnClickOutside: true,
        onClose: closeModal,
        width: 700,
      }
    );
  };

  const fetchData = useCallback(
    (pageNumber, pageSize, sortBy) =>
      getEntityList({ pageNumber, pageSize, sortBy }),
    []
  );

  const CAN_EXECUTE =
    dataSource && dataSource.cdmSchema && dataSource.resultSchema;

  const executions = useMemo(() => Object.values(data.byId) || [], [data.byId]);
  return (
    <Grid container p={2}>
      <Grid item xs={12} display="flex" justifyContent="space-between">
        <Typography variant="h2">
          {t('modules.administration.databases.jobs_header')}
        </Typography>
        <Tooltip
          placement="bottom"
          text={CAN_EXECUTE ? undefined : <>{t('messages.can_execute')}</>}
        >
          <div>
            <Button
              variant="contained"
              startIcon={<Icon iconName="submit" />}
              onClick={handleExecute}
              color="success"
              sx={{ px: 2 }}
              disabled={!CAN_EXECUTE}
            >
              {t('buttons.configure_cdm_schema')}
            </Button>
          </div>
        </Tooltip>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          '.c-table': {
            display: executions?.length ? 'block' : 'none',
          },
        }}
      >
        <Table
          manualSortBy
          enablePagination
          manualPagination
          columns={columns()}
          data={executions}
          onRowClick={row => {
            navigate('executions/' + row.original.id);
          }}
          disableRowClick={row => row.original.status == null}
          fetchData={fetchData}
          totalElements={totalElements}
          pageCount={pageCount}
          isLoading={
            status === Status.IN_PROGRESS ||
            status === Status.IN_PROGRESS_RELOAD
          }
          noDataText={t('messages.no_executions')}
        />
      </Grid>
      <Routes>
        <Route
          path="executions/:submissionId/*"
          element={
            <Modal open onClose={() => navigate('.')} width="80vw">
              <ExecutionResults
                onClose={() => navigate('.')}
                dataSourceId={dataSourceId}
              />
            </Modal>
          }
        />
      </Routes>
    </Grid>
  );
};
